import StatusCell from 'components/table/StatusCell'
import TitleWithSubtitleCell from 'components/table/TitleWithSubtitleCell'
import { WppAvatarGroup } from '@wppopen/components-library-react'
import { setPosition, setTaskPosition } from 'reducers/PaginatedTableReducer/PaginatedTableReducer'
import { DEFAULT_API_ITEM_LIMITS, unknown } from 'utils/constants'
import { getFormattedDate, getTypeKey } from 'utils'
import type { Dispatch, SetStateAction } from 'react'
import type { FetchReq } from 'hooks/useApi'
import type { ColDef } from 'ag-grid-community'
import type { ConfigListData, ProjectsListData } from '@contracts'
import type { PaginatedTableReducerAction } from 'reducers/PaginatedTableReducer/PaginatedTableReducer.d'
import type { ClientAndBrandName, ExtractElementType } from 'utils/utils.d'
import MenuListPopUp from 'components/MenuListPopUp/MenuListPopUp'
import styles from './HomePage.module.scss'

export type SingleProject = ExtractElementType<NonNullable<ProjectsListData['projects']>>
export type SingleProjectTableData = SingleProject & ClientAndBrandName

export interface IStatusValues {
  id: string
  status: number | null
  completedJobs: number
  totalJobs: number
}

// Completed Projects Data types
export const projectsColumnStructure = (
  configRegions: ConfigListData['regions'] | undefined,
  onProjectDetailsDelete: Dispatch<SetStateAction<SingleProject | undefined>>,
  clientsWithArchiveProject: string[],
): ColDef<SingleProjectTableData>[] => [
  {
    headerName: 'Project',
    field: getTypeKey<SingleProjectTableData>('project_name'),
    flex: 1,
    sortable: true,
    resizable: true,
    cellRenderer: ({ data }: { data: SingleProjectTableData }) => {
      return <TitleWithSubtitleCell title={data.project_name} subTitle={`${data.client_name} | ${data.brand_name}`} />
    },
  },
  {
    headerName: 'Regions',
    field: getTypeKey<SingleProjectTableData>('regions'),
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: SingleProjectTableData }) => {
      const { regions } = data
      const allRegionsIds = regions?.map(({ region_id }) => region_id)
      if (!allRegionsIds || !configRegions) return null

      // in the config regions, find the region that matches the region_id in the project data and get the flags
      const flags = allRegionsIds?.map(regionId => {
        const region = configRegions?.find(({ _id }) => _id === regionId)
        return { name: region?.name || 'Not found', src: `https://react-circle-flags.pages.dev/${region?.flag}.svg` }
      })
      return (
        <div className={styles.avatar_group_wrapper}>
          <WppAvatarGroup
            maxAvatarsToDisplay={3}
            size="s"
            withTooltip
            className={styles.avatar_group}
            avatars={flags}
            tooltipConfig={{}}
          />
        </div>
      )
    },
  },
  {
    headerName: 'Created',
    field: getTypeKey<SingleProjectTableData>('created_at'),
    flex: 1,
    sortable: true,
    resizable: true,
    valueFormatter: ({ value }) => getFormattedDate(value),
    cellRenderer: (colData: { valueFormatted: string; data: SingleProjectTableData }) => {
      const { data, valueFormatted } = colData
      const userName = data?.update_info?.user_name || unknown
      return <TitleWithSubtitleCell title={valueFormatted} subTitle={userName} />
    },
  },
  {
    field: getTypeKey<SingleProjectTableData>('status'),
    flex: 1,
    sortable: true,
    resizable: true,
    cellRenderer: ({ data }: { data: SingleProjectTableData }) => {
      const { status, job_ready_for_review_count } = data
      const label = status === 'ready_for_review' ? `${job_ready_for_review_count || 0} Pending Approval` : undefined
      return <StatusCell status={status} label={label} isProject />
    },
  },
  {
    headerName: '',
    field: 'actions',
    width: 75,
    resizable: true,
    cellRenderer: ({ data }: { data: SingleProjectTableData }) => {
      const canDelete = clientsWithArchiveProject.includes(data.client_id)
      if (!canDelete) return null
      return (
        <MenuListPopUp
          theme="transparent-border"
          className={styles.table_actions}
          buttonSize="s"
          menuItems={[
            {
              label: 'Delete',
              onClick: () => onProjectDetailsDelete(data),
            },
          ]}
        />
      )
    },
  },
]

export const handleLoadMore = (
  projectData: ProjectsListData,
  dispatch: (value: PaginatedTableReducerAction<unknown>) => void,
  setDataUrl: Dispatch<SetStateAction<URL>>,
  dataUrl: string | URL,
  setShouldDataAppend: (value: SetStateAction<boolean>) => void,
) => {
  if (!projectData) return
  const { position, item_count } = projectData
  const newPosition = position! + item_count!

  // send new position to reducer
  dispatch(setTaskPosition(newPosition))

  // then will use the setPosition function to do the behind the scenes api call
  setPosition(dataUrl, setDataUrl, newPosition)
  setShouldDataAppend(true)
}

// refresh functionality
export const refreshProjects = (
  // get number of row in table
  numberOfItemsInTable: number | undefined,
  dataUrl: string | URL,
  setInitialLoading: Dispatch<SetStateAction<boolean>>,
  fetchData: FetchReq<ProjectsListData>,
) => {
  const crateNewUrl = new URL(dataUrl)

  // if there is no data in the table, set initial loading to true and fetch default limit
  if (!numberOfItemsInTable) {
    setInitialLoading(true)
  } else {
    crateNewUrl.searchParams.set(
      'limit',
      // if the number of rows are 0 (there is no data), use default limit (25), otherwise use the number of rows
      numberOfItemsInTable === 0 ? DEFAULT_API_ITEM_LIMITS.PROJECTS_API.toString() : numberOfItemsInTable.toString(),
    )
    // position will always be 0
    crateNewUrl.searchParams.set('position', '0')
  }

  // fetch data because it doesn't change the data url state
  fetchData(crateNewUrl)
}
